import { useState } from 'react';
import { useConditionalEffect, usePrevious } from 'react-delta';
import classNames from 'classnames';
import Expand from '@mui/icons-material/KeyboardArrowRight';
import Collapse from '@mui/icons-material/KeyboardArrowDown';

import { DataNode } from 'common/types';

import './Tree.scss';

type TreeNodeProps = {
  node: DataNode;
  level?: number;
  indent?: number;
  itemClassName?: string;
  keyClassName?: string;
  valueClassName?: string;
  isExpanded?: boolean;
};

/**
 * Show a single node, which has a key (except for root), possibly a value, and zero or more child nodes.
 * Nodes will generally have either a value or children, but not both.
 *
 * @prop node             node to display
 * @prop level            depth of tree (used to control indenting)
 * @prop indent           number of px to indent each level (defaults to 20)
 * @prop itemClassName    class for each row
 * @prop keyClassName     class for keys
 * @prop valueClassName   class for values
 * @prop isExpanded       whether the node is expanded
 */
function TreeNode({
  node,
  itemClassName,
  keyClassName,
  valueClassName,
  level = 0,
  indent = 20,
  isExpanded = false,
}: TreeNodeProps): JSX.Element {
  const [isExpandedInternal, setIsExpandedInternal] = useState(isExpanded);
  const previousIsExpanded = usePrevious(isExpanded);

  useConditionalEffect(() => {
    setIsExpandedInternal(isExpanded);
  }, previousIsExpanded !== isExpanded && isExpanded !== isExpandedInternal);

  if (!node) {
    return <></>;
  }

  // clicking expansion icon toggles display of children
  const handleClick = () => {
    setIsExpandedInternal(!isExpandedInternal);
  };

  // expansion icon
  let icon = null;
  if (node.children && node.children.length > 0) {
    const ToggleIcon = isExpandedInternal ? Collapse : Expand;
    icon = <ToggleIcon fontSize="small" onClick={() => handleClick()} className="Tree__ExpandArrow" />;
  }

  // simulate list indentation
  const style = { paddingLeft: (level - 1) * indent + 'px' };

  // icon, key, and value if present
  const content = !node.key ? (
    <></>
  ) : (
    <>
      <div className={classNames('Tree__Item', itemClassName, { 'Tree__Item--Childless': !icon })}>
        <div style={style} className={classNames('Tree__Key', keyClassName)}>
          {icon}
          {node.key}
        </div>
        <div className={classNames('Tree__Value', valueClassName)}>{node.value}</div>
      </div>
    </>
  );

  // show children if expanded (root is always expanded)
  let childList = null;
  if (node.children && (level === 0 || isExpandedInternal)) {
    childList = node.children.map((child, idx) => (
      <TreeNode
        node={child}
        key={'node-' + idx}
        level={level + 1}
        indent={indent}
        itemClassName={itemClassName}
        keyClassName={keyClassName}
        valueClassName={valueClassName}
        isExpanded={isExpanded}
      />
    ));
  }

  return (
    <>
      {content}
      {childList}
    </>
  );
}

type TreeProps = {
  root: DataNode;
  className?: string;
  itemClassName?: string;
  keyClassName?: string;
  valueClassName?: string;
  indent?: number;
  isExpanded?: boolean;
};

/**
 * Display a tree of key/value pairs, starting with the root. Each child node has a key and either a value or children.
 * Root only has children and is always expanded.
 *
 * @prop root             root of the tree
 * @prop className        class for the tree
 * @prop itemClassName    class for each row
 * @prop keyClassName     class for keys
 * @prop valueClassName   class for values
 * @prop indent           number of px to indent each level (defaults to 20)
 * @prop isExpanded       whether all nodes should be expanded
 */
function Tree({
  root,
  className,
  itemClassName,
  keyClassName,
  valueClassName,
  indent,
  isExpanded = false,
}: TreeProps): JSX.Element {
  return (
    <div className={classNames('Tree', className)}>
      <TreeNode
        node={root}
        indent={indent}
        itemClassName={itemClassName}
        keyClassName={keyClassName}
        valueClassName={valueClassName}
        isExpanded={isExpanded}
      />
    </div>
  );
}

export default Tree;
